import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import dayjs from "dayjs";
import { ChevronDown, ChevronLeft, ChevronRight, Clock, Pencil, Check } from "lucide-react";

// ─── Design tokens ────────────────────────────────────────────────────────────
const PURPLE_GRADIENT = "linear-gradient(2deg, #A28FDC 0.74%, #7C3AED 74.61%)";
const PURPLE_SOLID    = "#7C3AED";
const PURPLE_LIGHT_BG = "#EDE8FC";
const DAY_LABELS      = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const SUB_DURATION = { "7days": { min: 7, max: 29 }, "30days": { min: 30, max: 90 } };
const MODE_LABEL   = { daily: "Daily Rental", ev: "EV · Daily Rental", "7days": "7 Days+ Subscription", "30days": "30 Days+ Subscription" };

const STEP_TITLE = {
  "city":         "Select City",
  "pickup-date":  "Select pickup date",
  "pickup-time":  "Select pickup time",
  "dropoff-date": "Select drop date",
  "dropoff-time": "Select drop time",
  "summary":      "Review Your Search",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
function roundToNearest30(t) {
  const mins = t.minute();
  if (mins < 15) return t.minute(0).second(0);
  if (mins < 45) return t.minute(30).second(0);
  return t.add(1, "hour").minute(0).second(0);
}

function roundUpToHour(t) {
  return t.minute() === 0 ? t.second(0) : t.add(60 - t.minute(), "minute").second(0);
}

const atTime = (d, t) => {
  const [h, m] = t.split(":").map(Number);
  return d.hour(h).minute(m).second(0).millisecond(0);
};

function buildCalendarDays(month) {
  const start = month.startOf("month");
  const end   = month.endOf("month");
  const days  = [];
  for (let i = 0; i < start.day(); i++) days.push(null);
  for (let d = start; !d.isAfter(end); d = d.add(1, "day")) days.push(d.clone());
  return days;
}

function generateSlots(hourlyOnly) {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (const m of (hourlyOnly ? [0] : [0, 30])) {
      const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      slots.push({ value, label: dayjs(`2000-01-01 ${value}`).format("h:mm A") });
    }
  }
  return slots;
}

const DAILY_SLOTS = generateSlots(false);
const SUB_SLOTS   = generateSlots(true); // subscription slots are hourly

// EV has its own buffer / min hours; subscriptions share the normal ones
const cfgFromRow = (row, isEv) => ({
  bufferTime:    (isEv ? row?.bufferTimeEvCar : row?.bufferTime)    ?? 3,
  minBookingHrs: (isEv ? row?.minBookingHrsEv : row?.minBookingHrs) ?? 18,
});

// ─── Component ────────────────────────────────────────────────────────────────
// Full-screen, step-by-step search for the mobile listing page:
// city (+hub) → pick-up date → pick-up time → drop-off date → drop-off time (not for subscriptions) → summary.
// Works on a draft; nothing reaches the listing until "Confirm & Search".
const SearchOverlayMob = ({
  open, onClose, onConfirm, mode, cities = [],
  cityId: cityIdProp, hubId: hubIdProp, hubs: hubsProp = [],
  bufferTime: bufferTimeProp, minBookingHrs: minBookingHrsProp,
  pickupDate: pickupDateProp, pickupTime: pickupTimeProp, dropDate: dropDateProp, dropTime: dropTimeProp,
}) => {
  const sub   = SUB_DURATION[mode];
  const isEv  = mode === "ev";
  const STEPS = ["city", "pickup-date", "pickup-time", "dropoff-date", ...(sub ? [] : ["dropoff-time"]), "summary"];
  const SLOTS = sub ? SUB_SLOTS : DAILY_SLOTS;

  const [step,        setStep]        = useState("city");
  const [fromSummary, setFromSummary] = useState(false);
  const [cityId,      setCityId]      = useState("");
  const [hubs,        setHubs]        = useState([]);
  const [hubId,       setHubId]       = useState("");
  const [cfg,         setCfg]         = useState({ bufferTime: 3, minBookingHrs: 18 });
  const [cityLoading, setCityLoading] = useState(false);
  const [cityMenuOpen, setCityMenuOpen] = useState(false);
  const [pickup,      setPickup]      = useState(null);
  const [drop,        setDrop]        = useState(null);
  const [calMonth,    setCalMonth]    = useState(() => dayjs().startOf("month"));
  const citySeq = useRef(0);

  // ── Start every open from the listing's current search ─────────────────────
  useEffect(() => {
    if (!open) return;
    citySeq.current++;
    setStep("city");
    setFromSummary(false);
    setCityId(String(cityIdProp || ""));
    setHubs(hubsProp);
    setHubId(String(hubIdProp || ""));
    setCfg({ bufferTime: bufferTimeProp ?? 3, minBookingHrs: minBookingHrsProp ?? 18 });
    setCityLoading(false);
    setCityMenuOpen(false);
    const p = pickupDateProp && pickupTimeProp ? atTime(pickupDateProp, pickupTimeProp) : null;
    const d = dropDateProp && dropTimeProp ? atTime(dropDateProp, dropTimeProp) : null;
    const rules = { bufferTime: bufferTimeProp ?? 3, minBookingHrs: minBookingHrsProp ?? 18 };
    const fixed = normalize(rules, p, d);
    setPickup(fixed.pickup);
    setDrop(fixed.drop);
  }, [open]); // eslint-disable-line

  // ── Lock page scroll behind the overlay ────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // ── Rules ──────────────────────────────────────────────────────────────────
  const earliestFor = (rules) => {
    const t = dayjs().add(rules.bufferTime, "hour");
    return (sub ? roundUpToHour(t) : roundToNearest30(t)).millisecond(0);
  };

  // Keeps a valid drop-off as is; only pushes it forward (or clamps a subscription to its day range)
  const fixDrop = (rules, p, d) => {
    if (sub) {
      const mirrored = d ? atTime(d, p.format("HH:mm")) : null; // drop time mirrors pickup time
      const min = p.add(sub.min, "day");
      const max = p.add(sub.max, "day");
      if (!mirrored || mirrored.isBefore(min)) return min;
      return mirrored.isAfter(max) ? max : mirrored;
    }
    const min = p.add(rules.minBookingHrs, "hour");
    return !d || d.isBefore(min) ? min : d;
  };

  // Only the parts the rules break move forward
  function normalize(rules, p, d) {
    const earliest = earliestFor(rules);
    let next = !p || p.isBefore(earliest) ? earliest : p;
    if (sub) next = roundUpToHour(next);
    return { pickup: next, drop: fixDrop(rules, next, d) };
  }

  // ── Step navigation ────────────────────────────────────────────────────────
  const goTo = setStep;

  // Calendar opens on the selected month — after the step's state has settled
  useEffect(() => {
    if (step === "pickup-date"  && pickup) setCalMonth(pickup.startOf("month"));
    if (step === "dropoff-date" && drop)   setCalMonth(drop.startOf("month"));
  }, [step]); // eslint-disable-line

  // Auto-advance. Coming from the summary, a finished edit returns there
  // (a date still goes through its time step, since the time depends on it).
  const advance = (from = step) => {
    if (fromSummary && (from === "city" || from.endsWith("-time"))) {
      setFromSummary(false);
      goTo("summary");
      return;
    }
    goTo(STEPS[STEPS.indexOf(from) + 1]);
  };

  const goBack = () => {
    if (fromSummary) { setFromSummary(false); goTo("summary"); return; }
    const idx = STEPS.indexOf(step);
    if (idx <= 0) onClose();
    else goTo(STEPS[idx - 1]);
  };

  const editFromSummary = (target) => {
    setFromSummary(true);
    goTo(target);
  };

  // ── City + hub ─────────────────────────────────────────────────────────────
  const applyCity = (id, rows, rules) => {
    setHubs(rows);
    setHubId(rows.length ? String(rows[0].id) : "");
    setCfg(rules);
    // a new city starts from its own defaults
    const fixed = normalize(rules, null, null);
    setPickup(fixed.pickup);
    setDrop(fixed.drop);
    setCityLoading(false);
    // a single hub has nothing to choose — move straight on
    if (rows.length < 2) advance("city");
  };

  const handleCityClick = (id) => {
    const key = String(id);
    if (key === cityId && !cityLoading) {
      if (hubs.length < 2) advance("city");
      return;
    }
    const seq = ++citySeq.current;
    setCityId(key);
    setHubs([]);
    setHubId("");
    // the listing's own city: reuse what it already loaded
    if (key === String(cityIdProp)) {
      applyCity(key, hubsProp, { bufferTime: bufferTimeProp ?? 3, minBookingHrs: minBookingHrsProp ?? 18 });
      if (hubsProp.some(h => String(h.id) === String(hubIdProp))) setHubId(String(hubIdProp));
      return;
    }
    setCityLoading(true);
    Promise.all([
      fetch(`${API_BASE}/api/hubs?limit=100&cityId=${key}`).then(r => r.json()).catch(() => null),
      fetch(`${API_BASE}/api/bookingconfigs?cityId=${key}`).then(r => r.json()).catch(() => null),
    ]).then(([hubRes, cfgRes]) => {
      if (seq !== citySeq.current) return; // a newer city was picked
      const rows = hubRes?.statusCode === "0100" ? hubRes.entities.rows : [];
      const row  = cfgRes?.statusCode === "0100" ? cfgRes.entities.rows[0] : null;
      applyCity(key, rows, cfgFromRow(row, isEv));
    });
  };

  const handleHubClick = (id) => {
    setHubId(String(id));
    advance("city");
  };

  // ── Dates + times ──────────────────────────────────────────────────────────
  const earliest = earliestFor(cfg);
  const lastSlot = SLOTS[SLOTS.length - 1].value;
  const isPickupStep = step.startsWith("pickup");

  const isDayDisabled = (day) => {
    // no slot left on this day once the buffer is applied
    if (atTime(day, lastSlot).isBefore(earliest)) return true;
    if (isPickupStep || !pickup) return false;
    if (sub) return day.isBefore(pickup.add(sub.min, "day"), "day") || day.isAfter(pickup.add(sub.max, "day"), "day");
    // no slot on this day reaches the minimum booking duration
    return atTime(day, lastSlot).isBefore(pickup.add(cfg.minBookingHrs, "hour"));
  };

  const handleDayClick = (day) => {
    if (isPickupStep) {
      const picked = atTime(day, pickup.format("HH:mm"));
      const next   = picked.isBefore(earliest) ? earliest : picked;
      setPickup(next);
      setDrop(fixDrop(cfg, next, drop));
    } else if (sub) {
      setDrop(atTime(day, pickup.format("HH:mm")));
    } else {
      const minDrop = pickup.add(cfg.minBookingHrs, "hour");
      const picked  = atTime(day, drop.format("HH:mm"));
      setDrop(picked.isBefore(minDrop) ? minDrop : picked);
    }
    advance();
  };

  const handleSlotClick = (value) => {
    if (isPickupStep) {
      const next = atTime(pickup, value);
      setPickup(next);
      setDrop(fixDrop(cfg, next, drop));
    } else {
      setDrop(atTime(drop, value));
    }
    advance();
  };

  const availableSlots = () => {
    if (isPickupStep) return SLOTS.filter(({ value }) => !atTime(pickup, value).isBefore(earliest));
    const minDrop = pickup.add(cfg.minBookingHrs, "hour");
    return SLOTS.filter(({ value }) => !atTime(drop, value).isBefore(minDrop));
  };

  const getDuration = () => {
    if (!pickup || !drop) return "";
    let diff = drop.diff(pickup, "minute");
    if (diff <= 0) return "—";
    const days  = Math.floor(diff / 1440); diff -= days * 1440;
    const hours = Math.floor(diff / 60);
    const mins  = diff % 60;
    return [days && `${days}d`, hours && `${hours}h`, mins && `${mins}m`].filter(Boolean).join(" ");
  };

  const handleConfirm = () => {
    onConfirm({ cityId, hubId, pickup, drop });
    onClose();
  };

  if (!open || !pickup || !drop) return null;

  const cityName   = cities.find(c => String(c.id) === cityId)?.name || "";
  const hubName    = hubs.find(h => String(h.id) === hubId)?.hubName || "";
  const stepIndex  = STEPS.indexOf(step);
  const minMonth   = dayjs().startOf("month");
  const calDays    = buildCalendarDays(calMonth);
  const activeTime = isPickupStep ? pickup.format("HH:mm") : drop.format("HH:mm");

  // ── Render pieces ──────────────────────────────────────────────────────────
  const renderCityStep = () => (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <span className="text-[#64748B] text-[12px] font-semibold uppercase tracking-[0.5px] text-left">City</span>
        {/* Dropdown — the list opens inline so it never clips inside the scroll area */}
        <div className="flex flex-col rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => setCityMenuOpen(o => !o)}
            className="h-12 px-3 flex items-center justify-between gap-2 text-left"
          >
            <span className={`text-[15px] truncate ${cityName ? "text-[#121212] font-semibold" : "text-[#9CA3AF] font-medium"}`}>
              {cityName || "Select city"}
            </span>
            <ChevronDown size={18} className={`shrink-0 text-[#374151] transition-transform duration-200 ${cityMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {cityMenuOpen && (
            <div className="border-t border-[#f1f1f4] max-h-[280px] overflow-y-auto overscroll-contain py-1">
              {cities.map(c => {
                const active = String(c.id) === cityId;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { setCityMenuOpen(false); handleCityClick(c.id); }}
                    className={`w-full px-3 py-3 flex items-center justify-between gap-2 text-[14px] text-left ${active ? "text-[#7C3AED] font-semibold bg-[#F5F3FF]" : "text-[#121212] font-medium"}`}
                  >
                    <span className="truncate">{c.name}</span>
                    {active && <Check size={16} className="shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-[#64748B] text-[12px] font-semibold uppercase tracking-[0.5px] text-left">City Hub</span>
        {cityLoading ? (
          <div className="h-11 rounded-xl bg-[#f3f4f6] animate-pulse" />
        ) : hubs.length === 0 ? (
          <span className="text-[#9CA3AF] text-[13px] text-left">No hubs available in this city</span>
        ) : hubs.length < 2 ? (
          // a single hub is shown for reference only
          <div className="h-11 px-3 rounded-xl border border-[#e5e7eb] bg-[#f9fafb] flex items-center text-[14px] font-medium text-[#374151] truncate">
            {hubs[0].hubName}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {hubs.map(h => {
              const active = String(h.id) === hubId;
              return (
                <button
                  key={h.id}
                  type="button"
                  onClick={() => handleHubClick(h.id)}
                  className={`min-h-11 px-3 py-2.5 rounded-xl border flex items-center justify-between gap-2 text-[14px] text-left transition-colors ${active ? "border-[#7C3AED] bg-[#F5F3FF] text-[#7C3AED] font-semibold" : "border-[#e5e7eb] bg-white text-[#121212] font-medium"}`}
                >
                  <span className="min-w-0 break-words">{h.hubName}</span>
                  {active && <Check size={16} className="shrink-0" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderCalendar = () => (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-[#0f172a]">{calMonth.format("MMMM YYYY")}</span>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={!calMonth.isAfter(minMonth)}
            onClick={() => setCalMonth(m => m.subtract(1, "month"))}
            className="p-2 rounded-lg disabled:opacity-30"
          >
            <ChevronLeft size={16} className="text-[#6b7280]" />
          </button>
          <button type="button" onClick={() => setCalMonth(m => m.add(1, "month"))} className="p-2 rounded-lg">
            <ChevronRight size={16} className="text-[#6b7280]" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 mb-1">
        {DAY_LABELS.map(d => <div key={d} className="text-center text-[11px] text-[#9ca3af] py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {calDays.map((day, i) => {
          if (!day) return <div key={i} className="h-10" />;
          const isPU     = day.isSame(pickup, "day");
          const isDO     = day.isSame(drop, "day");
          const inRange  = day.isAfter(pickup, "day") && day.isBefore(drop, "day");
          const disabled = isDayDisabled(day);
          let bg = "transparent", color = disabled ? "#d1d5db" : "#374151";
          if (isPU)                           { bg = PURPLE_GRADIENT; color = "white"; }
          else if (!isPickupStep && isDO)     { bg = "#1e1b4b";       color = "white"; }
          else if (!isPickupStep && inRange)  { bg = PURPLE_LIGHT_BG; color = PURPLE_SOLID; }
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => handleDayClick(day)}
              className="h-10 w-10 mx-auto rounded-full text-[14px] flex items-center justify-center"
              style={{ background: bg, color, fontWeight: isPU || isDO ? 600 : 400 }}
            >
              {day.date()}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderTimes = () => (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Clock size={15} style={{ color: PURPLE_SOLID }} />
        <span className="font-semibold text-[#0f172a]">
          {isPickupStep ? pickup.format("ddd, D MMM") : drop.format("ddd, D MMM")}
        </span>
      </div>
      <div className="grid grid-cols-3 min-[380px]:grid-cols-4 gap-2">
        {availableSlots().map(slot => {
          const active = slot.value === activeTime;
          return (
            <button
              key={slot.value}
              type="button"
              onClick={() => handleSlotClick(slot.value)}
              className="w-full py-2.5 rounded-xl text-[12px] border"
              style={active
                ? { background: isPickupStep ? PURPLE_GRADIENT : "#1e1b4b", color: "white", borderColor: "transparent" }
                : { background: "white", color: "#374151", borderColor: "#e5e7eb" }}
            >
              {slot.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const SummaryRow = ({ label, value, target, note }) => (
    <button
      type="button"
      disabled={!target}
      onClick={() => target && editFromSummary(target)}
      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left disabled:cursor-default"
    >
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[#64748B] text-[11px] font-semibold uppercase tracking-[0.5px]">{label}</span>
        <span className="text-[#121212] text-[15px] font-semibold break-words">{value}</span>
        {note && <span className="text-[#9CA3AF] text-[11px]">{note}</span>}
      </div>
      {target && (
        <span className="w-8 h-8 shrink-0 rounded-full bg-[#F5F3FF] flex items-center justify-center">
          <Pencil size={14} style={{ color: PURPLE_SOLID }} />
        </span>
      )}
    </button>
  );

  const renderSummary = () => (
    <div className="flex flex-col gap-4">
      <div className="rounded-2xl border border-[#e5e7eb] bg-white divide-y divide-[#f1f1f4] overflow-hidden">
        <SummaryRow label="City & Hub"    value={[cityName, hubName].filter(Boolean).join(" · ")} target="city" />
        <SummaryRow label="Pick-up Date"  value={pickup.format("ddd, D MMM YYYY")}                target="pickup-date" />
        <SummaryRow label="Pick-up Time"  value={pickup.format("h:mm A")}                         target="pickup-time" />
        <SummaryRow label="Drop-off Date" value={drop.format("ddd, D MMM YYYY")}                  target="dropoff-date" />
        {sub
          ? <SummaryRow label="Drop-off Time" value={drop.format("h:mm A")} note="Same as pick-up time" />
          : <SummaryRow label="Drop-off Time" value={drop.format("h:mm A")} target="dropoff-time" />}
      </div>
      <div className="flex items-center gap-2 bg-[#EDE8FC] rounded-xl px-4 py-2.5">
        <Clock size={14} style={{ color: PURPLE_SOLID }} />
        <span className="text-sm font-semibold text-[#1e1b4b]">Trip Duration: {getDuration()}</span>
      </div>
    </div>
  );

  const continueDisabled = step === "city" && (cityLoading || !hubId);

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9998] bg-white flex flex-col w-full h-screen overflow-hidden font-['Outfit']"
      style={{ height: "100dvh" }}
    >
      {/* Header */}
      <div className="shrink-0 border-b border-[#f1f1f4]">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3">
          <button type="button" onClick={goBack} className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-[#f3f4f6] text-[#374151]">
            <ChevronLeft size={18} />
          </button>
          <div className="flex flex-col min-w-0 flex-1 text-left">
            <span className="text-[#0f172a] text-[17px] font-semibold leading-tight truncate">{STEP_TITLE[step]}</span>
            <span className="text-[#7C3AED] text-[12px] font-medium truncate">{MODE_LABEL[mode] || ""}</span>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 shrink-0 flex items-center justify-center rounded-full bg-[#f3f4f6] text-[#374151]">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        </div>
        {/* Progress */}
        <div className="flex gap-1 px-4 pb-3">
          {STEPS.map((s, i) => (
            <div key={s} className="h-1 flex-1 rounded-full" style={{ background: i <= stepIndex ? PURPLE_SOLID : "#e5e7eb" }} />
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-4">
        {step === "city" && renderCityStep()}
        {step.endsWith("-date") && renderCalendar()}
        {step.endsWith("-time") && renderTimes()}
        {step === "summary" && renderSummary()}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-4 pt-3 border-t border-[#f1f1f4] bg-white" style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}>
        {step === "summary" ? (
          <button
            type="button"
            onClick={handleConfirm}
            className="w-full h-12 rounded-xl text-white text-[15px] font-bold"
            style={{ background: PURPLE_GRADIENT }}
          >
            Confirm & Search
          </button>
        ) : (
          <button
            type="button"
            disabled={continueDisabled}
            onClick={() => advance()}
            className="w-full h-12 rounded-xl text-[15px] font-semibold border border-[#7C3AED] text-[#7C3AED] bg-white disabled:opacity-40"
          >
            {fromSummary && (step === "city" || step.endsWith("-time")) ? "Back to Summary" : "Continue"}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

export default SearchOverlayMob;
